import './DraggerUploadInput.scss';

import { mdiTrayArrowDown } from '@mdi/js';
import type { UploadProps } from 'antd';
import { Upload } from 'antd';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import { handleFilenameLength } from 'helpers/attachmentsHelpers';
import { getAccessToken, getDeviceId } from 'helpers/tokenManager';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { InputNoteConfig } from '../_common';
import {
  getHookFieldClass,
  getHookGroupClass,
  renderErrorMessage,
  renderHookInputHeader
} from '../_common';

interface DraggerUploadInputProps {
  name: string;
  uploadPath: string;
  label?: string;
  required?: boolean;
  inputNote?: InputNoteConfig;
  options?: RegisterOptions;
  multiple?: boolean;
  hideLabel?: boolean;
}

const DraggerUploadInput = ({
  name,
  uploadPath,
  label,
  required,
  inputNote,
  options,
  multiple,
  hideLabel
}: DraggerUploadInputProps) => {
  const { tInput } = useCustomTranslation();

  const { control, getFieldState, formState } = useFormContext();

  const { invalid, isDirty, error, isTouched } = getFieldState(name, formState);

  const showError = !!error && isDirty;

  const uploadProps: UploadProps = {
    action: uploadPath,
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
      deviceId: getDeviceId()
    },
    multiple,
    beforeUpload: handleFilenameLength
  };

  return (
    <div className={getHookGroupClass(label, 'DraggerUploadInput')}>
      {!hideLabel && renderHookInputHeader(name, label, required, inputNote)}
      <Controller
        name={name}
        control={control}
        rules={options}
        render={({ field }) => (
          <Upload
            {...uploadProps}
            {...field}
            id={name}
            name="file"
            fileList={field.value}
            onChange={(info) => field.onChange(info.fileList)}
            onRemove={(file) =>
              field.onChange(field.value?.filter((x: any) => x.uploaderId !== file.uid))
            }
            className={getHookFieldClass(isTouched, invalid)}
          >
            <Button icon={<Icon path={mdiTrayArrowDown} />}> {tInput('upload_attachment')}</Button>
          </Upload>
        )}
      />
      {renderErrorMessage(showError, error?.message)}
    </div>
  );
};

export default DraggerUploadInput;
